import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FText, FNumber, FSelect, FColor, FCheck } from './builderFields';

const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 };
const h3 = { gridColumn: '1 / -1', fontSize: '0.85rem', fontWeight: 800, color: '#18181B', margin: '8px 0 0', paddingTop: 10, borderTop: '1px dashed #E4E4E7' };
const firstH3 = { gridColumn: '1 / -1', fontSize: '0.85rem', fontWeight: 800, color: '#18181B', margin: 0 };

/** Choose a saved template or per-widget custom design. */
export function TemplateSelect({ kind = 'product', value, onChange }) {
  const [tpls, setTpls] = useState([]);
  useEffect(() => {
    axios.get('/site-settings').then(r => setTpls(r.data?.list_templates?.[kind] || [])).catch(() => {});
  }, [kind]);
  return (
    <FSelect label="قالب نمایش" value={value || 'custom'} onChange={onChange}
      options={[
        { value: 'custom', label: 'طراحی اختصاصی همین بخش' },
        ...tpls.map(t => ({ value: String(t.id), label: `قالب «${t.name}»${t.isDefault ? ' (پیش‌فرض)' : ''}` })),
      ]} />
  );
}

/** Full card/list design editor. value = tpl object, onChange(patch) merges. */
export function ListTplEditor({ kind = 'product', value = {}, onChange }) {
  const v = value || {};
  const up = patch => onChange({ ...v, ...patch });
  const isPost = kind === 'post';
  return (
    <div style={grid2}>
      <div style={firstH3}>چیدمان لیست</div>
      <FSelect label="حالت نمایش" value={v.layout || 'grid'} onChange={x => up({ layout: x })}
        options={[
          { value: 'grid', label: 'شبکه عمودی (چند ستون)' },
          { value: 'scroll', label: 'افقی اسکرول‌پذیر' },
          { value: 'list', label: 'لیست عمودی (ردیفی)' },
        ]} />
      {(v.layout || 'grid') === 'grid' && (
        <>
          <FSelect label="ستون دسکتاپ" value={String(v.columns ?? 4)} onChange={x => up({ columns: Number(x) })}
            options={['2', '3', '4', '5'].map(n => ({ value: n, label: `${n} ستون` }))} />
          <FSelect label="ستون موبایل" value={String(v.columnsMobile ?? (isPost ? 1 : 2))} onChange={x => up({ columnsMobile: Number(x) })}
            options={['1', '2', '3'].map(n => ({ value: n, label: `${n} ستون` }))} />
        </>
      )}
      {(v.layout || 'grid') === 'scroll' && (
        <>
          <FNumber label="عرض کارت (px)" value={v.cardWidth ?? 250} onChange={x => up({ cardWidth: x })} min={160} max={480} />
          <div>
            <FCheck label="دکمه‌های عقب/جلو" checked={v.showNav !== false} onChange={x => up({ showNav: x })} />
          </div>
        </>
      )}
      <FNumber label="فاصله کارت‌ها (px)" value={v.gap ?? 16} onChange={x => up({ gap: x })} min={0} max={48} />

      <div style={h3}>کارت</div>
      <FColor label="پس‌زمینه کارت" value={v.bg} onChange={x => up({ bg: x })} />
      <FNumber label="ضخامت بوردر (px)" value={v.borderWidth ?? 0} onChange={x => up({ borderWidth: x })} min={0} max={6} />
      <FColor label="رنگ بوردر" value={v.borderColor} onChange={x => up({ borderColor: x })} />
      <FNumber label="گردی گوشه (px)" value={v.radius ?? 14} onChange={x => up({ radius: x })} min={0} max={48} />
      <FSelect label="سایه کارت" value={v.shadow || 'md'} onChange={x => up({ shadow: x })}
        options={[
          { value: 'none', label: 'بدون سایه' },
          { value: 'sm', label: 'کم' },
          { value: 'md', label: 'متوسط' },
          { value: 'lg', label: 'زیاد' },
          { value: 'xl', label: 'خیلی زیاد' },
        ]} />
      <FNumber label="فاصله داخلی (px)" value={v.padding ?? 12} onChange={x => up({ padding: x })} min={0} max={40} />
      <FSelect label="تراز متن" value={v.textAlign || 'right'} onChange={x => up({ textAlign: x })}
        options={[{ value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />

      <div style={h3}>تصویر</div>
      <FSelect label="جای تصویر" value={v.image || 'top'} onChange={x => up({ image: x })}
        options={[
          { value: 'top', label: 'بالای کارت' },
          { value: 'side', label: 'کنار متن (افقی)' },
          { value: 'background', label: 'پس‌زمینه کارت' },
          { value: 'none', label: 'بدون تصویر' },
        ]} />
      {(v.image || 'top') === 'top' && (
        <>
          <FNumber label="ارتفاع تصویر (px)" value={v.imageHeight ?? (isPost ? 170 : 190)} onChange={x => up({ imageHeight: x })} min={80} max={500} />
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: '0.78rem', fontWeight: 600, color: '#52525B', textAlign: 'right' }}>ارتفاع ثابت تصویر</label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
              <input type="checkbox" checked={!!v.imageFixedHeight} onChange={e => up({ imageFixedHeight: e.target.checked })} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> ثابت (بدون تغییر با محتوا)
            </label>
          </div>
        </>
      )}
      {(v.image || 'top') === 'side' && (
        <FNumber label="عرض تصویر کناری (px)" value={v.sideWidth ?? (isPost ? 140 : 130)} onChange={x => up({ sideWidth: x })} min={80} max={300} />
      )}
      {(v.image || 'top') === 'background' && (
        <FNumber label="حداقل ارتفاع (px)" value={v.imageHeight ?? 220} onChange={x => up({ imageHeight: x })} min={160} max={500} />
      )}

      <div style={h3}>عنوان و توضیح</div>
      <FNumber label="اندازه عنوان (px)" value={v.titleSize ?? 15} onChange={x => up({ titleSize: x })} min={11} max={30} />
      <FColor label="رنگ عنوان" value={v.titleColor} onChange={x => up({ titleColor: x })} />
      <FSelect label="عنوان حداکثر چند خط" value={String(v.titleLines ?? 2)} onChange={x => up({ titleLines: Number(x) })}
        options={['1', '2', '3'].map(n => ({ value: n, label: `${n} خط` }))} />
      <div>
        <FCheck label="نمایش توضیحات" checked={!!v.showDesc} onChange={x => up({ showDesc: x })} />
      </div>
      {!!v.showDesc && (
        <>
          <FNumber label="اندازه توضیح (px)" value={v.descSize ?? (isPost ? 13 : 12)} onChange={x => up({ descSize: x })} min={10} max={20} />
          <FColor label="رنگ توضیح" value={v.descColor} onChange={x => up({ descColor: x })} />
          <FSelect label="توضیح حداکثر چند خط" value={String(v.descLines ?? 2)} onChange={x => up({ descLines: Number(x) })}
            options={['1', '2', '3', '4'].map(n => ({ value: n, label: `${n} خط` }))} />
        </>
      )}

      <div style={h3}>{isPost ? 'تاریخ و دکمه' : 'قیمت و دکمه'}</div>
      {!isPost && (
        <>
          <div>
            <FCheck label="نمایش قیمت" checked={v.showPrice !== false} onChange={x => up({ showPrice: x })} />
          </div>
          <FNumber label="اندازه قیمت (px)" value={v.priceSize ?? 14} onChange={x => up({ priceSize: x })} min={11} max={26} />
          <FColor label="رنگ قیمت" value={v.priceColor} onChange={x => up({ priceColor: x })} />
          <div>
            <FCheck label="برچسب ناموجودی" checked={v.showStock !== false} onChange={x => up({ showStock: x })} />
          </div>
        </>
      )}
      {isPost && (
        <div>
          <FCheck label="نمایش تاریخ" checked={v.showDate !== false} onChange={x => up({ showDate: x })} />
        </div>
      )}
      <div>
        <FCheck label="دکمه مشاهده" checked={!!v.showButton} onChange={x => up({ showButton: x })} />
      </div>
      {!!v.showButton && (
        <>
          <FText label="متن دکمه" value={v.buttonLabel} onChange={x => up({ buttonLabel: x })} placeholder={isPost ? 'ادامه مطلب' : 'مشاهده'} />
          <FSelect label="مدل دکمه" value={v.buttonStyle || 'outline'} onChange={x => up({ buttonStyle: x })}
            options={[{ value: 'outline', label: 'خطی' }, { value: 'solid', label: 'توپر' }]} />
        </>
      )}
    </div>
  );
}
